import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { withBackClose } from "./history-aware-root"
import {
  MobileSheet, MobileSheetBody, MobileSheetCloseButton, MobileSheetContent, MobileSheetDescription, MobileSheetFooter, MobileSheetHeader,
  MobileSheetTitle,
} from "./mobile-sheet"
import useIsMobile from "../../hooks/useIsMobile"

// Below md (useIsMobile), every dialog of the app opens as the shared bottom sheet (DESIGN_SYSTEM.md §23): the root becomes a
// MobileSheet (vaul, drag-to-close, Android Back closes it) and DialogContent splits its DIRECT children like Jamora's
// ResponsiveDialog — DialogHeader into the fixed header (with the 44 px « Fermer »), DialogFooter into the fixed footer, everything
// else into the one scrolling body. A header or footer nested deeper (inside a <form>) simply stays in the body. Desktop is unchanged.
const MobileDialog = React.createContext(false)
const DesktopRoot = withBackClose(DialogPrimitive.Root)

function Dialog(props) {
  const mobile = useIsMobile()
  const Root = mobile ? MobileSheet : DesktopRoot
  return <MobileDialog.Provider value={mobile}><Root {...props} /></MobileDialog.Provider>
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

function splitSlots(children) {
  const header = [], body = [], footer = []
  const visit = (nodes) => React.Children.toArray(nodes).forEach((child) => {
    if (React.isValidElement(child) && child.type === React.Fragment) visit(child.props.children)
    else if (React.isValidElement(child) && child.type === DialogHeader) header.push(child)
    else if (React.isValidElement(child) && child.type === DialogFooter) footer.push(child)
    else body.push(child)
  })
  visit(children)
  return { header, body, footer }
}

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const mobile = React.useContext(MobileDialog)
  if (mobile) {
    const { header, body, footer } = splitSlots(children)
    return (
      <MobileSheetContent ref={ref} closeOnHandle={header.length === 0} {...props}>
        {header.length > 0 && (
          <MobileSheetHeader>
            {header}
            <MobileSheetCloseButton />
          </MobileSheetHeader>
        )}
        {/* The caller's className (padding, gap…) applies to the body; width and height belong to the sheet. */}
        <MobileSheetBody className={cn("grid grid-cols-1 content-start gap-4", className, "!m-0 !w-full !max-w-none !max-h-none !overflow-y-auto")}>
          {body}
        </MobileSheetBody>
        {footer.length > 0 && <MobileSheetFooter>{footer}</MobileSheetFooter>}
      </MobileSheetContent>
    )
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid grid-cols-1 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => {
  const mobile = React.useContext(MobileDialog)
  return (
    <div
      className={mobile ? cn("min-w-0 flex-1 space-y-1 text-left", className) : cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
      {...props} />
  )
}
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => {
  const mobile = React.useContext(MobileDialog)
  return (
    <div
      className={mobile ? cn("flex flex-col-reverse gap-2 [&>button]:min-h-11 [&>button]:w-full", className, "!flex-col-reverse !space-x-0")
        : cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
      {...props} />
  )
}
DialogFooter.displayName = "DialogFooter"

// On a phone the title and description come from the same primitive as the sheet's root (vaul), so they always find its context.
const DialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  const mobile = React.useContext(MobileDialog)
  const Title = mobile ? MobileSheetTitle : DialogPrimitive.Title
  return <Title ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => {
  const mobile = React.useContext(MobileDialog)
  const Description = mobile ? MobileSheetDescription : DialogPrimitive.Description
  return <Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
